import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';

const API_BASE_URL = process.env.REACT_APP_API_URI;

// Transform service data helper function
const transformServiceData = (data) => {
  if (!data) {
    console.error('No data provided to transformServiceData');
    return null;
  }

  try {
    const transformed = {
      ...data,
      // Core service fields
      title: data.title || '',
      slug: data.slug || '',
      content: data.content || '',
      shortDescription: data.shortDescription || '',
      description: data.description || '',
      
      // Status and dates
      status: data.status || 'draft',
      publishedAt: data.publishedAt ? new Date(data.publishedAt) : null,
      createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
      updatedAt: data.updatedAt ? new Date(data.updatedAt) : new Date(),
      
      // Service metadata
      featureImage: data.featureImage || '',
      featured: data.featured || false,
      
      // Engagement metrics
      metadata: {
        views: data.metadata?.views || 0,
        likes: data.metadata?.likes || 0,
        shares: data.metadata?.shares || 0
      },

      hero: {
        headline: data.hero?.headline || '',
        subheadline: data.hero?.subheadline || '',
        ctaPrimary: {
          text: data.hero?.ctaPrimary?.text || '',
          link: data.hero?.ctaPrimary?.link || ''
        },
      }
    };

    return transformed;
  } catch (error) {
    console.error('Error transforming service data:', error);
    return null;
  }
};

// Get all services with pagination and sorting
export const fetchAllServices = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      limit: params.limit || 10,
      sortField: params.sortField || 'createdAt',
      sortOrder: params.sortOrder || 'desc',
      ...(params.status && { status: params.status }),
      ...(params.search && { search: params.search }),
      ...(params.featured && { featured: params.featured })
    }).toString();

    const response = await axios.get(
      `${API_BASE_URL}/api/services?${queryParams}`,
      { headers: getAuthHeaders() }
    );

    if (!response.data) {
      throw new Error('Invalid response from server');
    }

    // Ensure we have the correct data structure
    const services = response.data.data || [];
    const pagination = response.data.pagination || {
      total: services.length,
      pages: Math.ceil(services.length / (params.limit || 10)),
      limit: params.limit || 10
    };

    return {
      data: services.map(transformServiceData),
      pagination
    };
  } catch (error) {
    console.error('Error fetching services:', error);
    throw error.response?.data || error;
  }
};

// Get service by ID
export const getServiceById = async (id) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/services/${id}`,
      { headers: getAuthHeaders() }
    );
    
    if (!response.data || !response.data.data) {
      throw new Error('Invalid response structure from server');
    }

    return transformServiceData(response.data.data);
  } catch (error) {
    console.error('Error in getServiceById:', error);
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to fetch service');
    }
    throw error;
  }
};

// Create new service
export const createService = async (formData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/services`,
      formData,
      { headers: getAuthHeaders() }
    );
    return transformServiceData(response.data.data);
  } catch (error) {
    console.error('Error creating service:', error);
    if (error.response?.data?.errors) {
      const validationErrors = error.response.data.errors;
      const errorMessages = Object.values(validationErrors).flat();
      throw new Error(errorMessages.join(', '));
    }
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('An unexpected error occurred while creating the service');
  }
};

// Update service
export const updateService = async (id, formData) => {
  try {
   // console.log('### update formData = '+JSON.stringify(formData))
    const response = await axios.put(
      `${API_BASE_URL}/api/services/${id}`,
      formData,
      { headers: getAuthHeaders() }
    );
    return transformServiceData(response.data.data);
  } catch (error) {
    console.error('Error updating service:', error);
    throw error.response?.data || error;
  }
};

// Delete service
export const deleteService = async (id) => {
  try {
    await axios.delete(
      `${API_BASE_URL}/api/services/${id}`,
      { headers: getAuthHeaders() }
    );
    return true;
  } catch (error) {
    console.error('Error deleting service:', error);
    throw error.response?.data || error;
  }
};

// Update service status
export const updateServiceStatus = async (id, status) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/api/services/${id}/status`,
      { status },
      { headers: getAuthHeaders() }
    );
    return transformServiceData(response.data.data);
  } catch (error) {
    console.error('Error updating service status:', error);
    throw error.response?.data || error;
  }
};

// Get service by slug (public)
export const getServiceBySlug = async (slug) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/services/slug/${slug}`);
   // console.log('response.data getServiceBySlug = ', JSON.stringify(response.data));
    
    if (response.data.status === 'success' && response.data.data) {
      return transformServiceData(response.data.data);
    } else if (response.data.status === 'error') {
      throw new Error(response.data.message || 'Failed to fetch service');
    }
    
    return transformServiceData(response.data);
  } catch (error) {
    console.error('Error fetching service by slug:', error);
    throw error.response?.data || error;
  }
};

// Get published services (public)
export const fetchPublishedServices = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      limit: params.limit || 10,
      sortField: params.sortField || 'publishedAt',
      sortOrder: params.sortOrder || 'desc',
      ...(params.featured && { featured: params.featured })
    }).toString();

    const response = await axios.get(
      `${API_BASE_URL}/api/services/public?${queryParams}`
    );

    // Ensure we have a valid response structure
    if (!response.data) {
      throw new Error('Invalid response from server');
    }

    // Handle different response structures
    if (response.data.status === 'success' && Array.isArray(response.data.data)) {
      return {
        status: 'success',
        data: response.data.data.map(transformServiceData),
        pagination: response.data.pagination || {
          total: response.data.data.length,
          pages: 1,
          limit: params.limit || 10
        }
      };
    } else if (Array.isArray(response.data)) {
      return {
        status: 'success',
        data: response.data.map(transformServiceData),
        pagination: {
          total: response.data.length,
          pages: 1,
          limit: params.limit || 10
        }
      };
    } else {
      throw new Error('Invalid data structure in response');
    }
  } catch (error) {
    console.error('Error fetching published services:', error);
    return {
      status: 'error',
      message: error.response?.data?.message || error.message || 'Failed to fetch services',
      data: []
    };
  }
};

// Export services to CSV
export const exportServicesToCSV = async (services) => {
  try {
    const headers = ['Title', 'Slug', 'Description', 'Status', 'Created At', 'Updated At'];
    const rows = services.map(service => [
      service.title,
      service.slug,
      service.description,
      service.status,
      new Date(service.createdAt).toISOString(),
      new Date(service.updatedAt).toISOString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `services_${new Date().toISOString()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting services to CSV:', error);
    throw error;
  }
};

// Export services to JSON
export const exportServicesToJSON = async (services) => {
  try {
    const jsonContent = JSON.stringify(services, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `services_${new Date().toISOString()}.json`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting services to JSON:', error);
    throw error;
  }
};

// Import services from file
export const importServices = async (services, mode = 'merge') => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/services/import`,
      { services, mode },
      { headers: getAuthHeaders() }
    );

    return response.data;
  } catch (error) {
    console.error('Error importing services:', error);
    throw error.response?.data || error;
  }
}; 